import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Badge, Button, Card, CheckboxField, EmptyRow, PageHeader, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { PLAT, Plan, PlanArray, PlanCreate, PlanUpdate, keys } from './api';
import { BillingTabs, Money, MoneyField } from './parts';

interface Draft {
  code: string;
  name: string;
  monthlyPrice: number | null;
  annualPrice: number | null;
  allowances: { sites: number; kiosks: number; smsPerMonth: number; storageGb: number; modules: string };
  extras: { perSite: number | null; perKiosk: number | null; smsBundleSize: number; perSmsBundle: number | null; perStorageGb: number | null };
  modulePrices: { key: string; price: number | null }[];
  active: boolean;
}

const blank = (): Draft => ({
  code: '',
  name: '',
  monthlyPrice: 0,
  annualPrice: 0,
  allowances: { sites: 1, kiosks: 2, smsPerMonth: 0, storageGb: 5, modules: '' },
  extras: { perSite: 0, perKiosk: 0, smsBundleSize: 100, perSmsBundle: 0, perStorageGb: 0 },
  modulePrices: [],
  active: true,
});

const fromPlan = (p: Plan): Draft => ({
  code: p.code,
  name: p.name,
  monthlyPrice: p.monthlyPrice,
  annualPrice: p.annualPrice,
  allowances: { ...p.allowances, modules: p.allowances.modules.join(', ') },
  extras: { perSite: p.extras.perSite, perKiosk: p.extras.perKiosk, smsBundleSize: p.extras.smsBundleSize, perSmsBundle: p.extras.perSmsBundle, perStorageGb: p.extras.perStorageGb },
  modulePrices: Object.entries(p.extras.modules).map(([key, price]) => ({ key, price })),
  active: p.active,
});

function body(d: Draft) {
  const modules = Object.fromEntries(d.modulePrices.filter((m) => m.key.trim()).map((m) => [m.key.trim(), m.price ?? 0]));
  return {
    name: d.name,
    monthlyPrice: d.monthlyPrice ?? 0,
    annualPrice: d.annualPrice ?? 0,
    allowances: { ...d.allowances, modules: d.allowances.modules.split(',').map((m) => m.trim()).filter(Boolean) },
    extras: { ...d.extras, perSite: d.extras.perSite ?? 0, perKiosk: d.extras.perKiosk ?? 0, perSmsBundle: d.extras.perSmsBundle ?? 0, perStorageGb: d.extras.perStorageGb ?? 0, modules },
  };
}

function NumberField({ label, value, onChange, min = 0 }: { label: string; value: number; onChange: (n: number) => void; min?: number }) {
  return <TextField label={label} type="number" min={min} value={value} onChange={(e) => onChange(Math.max(min, Math.floor(Number(e.target.value) || 0)))} />;
}

function PlanEditor({ plan, onDone }: { plan: Plan | null; onDone: () => void }) {
  const [d, setD] = useState<Draft>(plan ? fromPlan(plan) : blank());
  const qc = useQueryClient();
  const toast = useToast();
  const save = useMutation({
    mutationFn: () => {
      const b = body(d);
      return plan
        ? api.patch(`${PLAT}/plans/${plan.id}`, { body: PlanUpdate.parse({ ...b, active: d.active }), schema: Plan })
        : api.post(`${PLAT}/plans`, { body: PlanCreate.parse({ ...b, code: d.code }), schema: Plan });
    },
    onSuccess: async () => {
      toast(t('billingUi.plans.saved'));
      await qc.invalidateQueries({ queryKey: keys.plans });
      onDone();
    },
  });
  const set = (patch: Partial<Draft>) => setD((s) => ({ ...s, ...patch }));
  const setA = (patch: Partial<Draft['allowances']>) => setD((s) => ({ ...s, allowances: { ...s.allowances, ...patch } }));
  const setE = (patch: Partial<Draft['extras']>) => setD((s) => ({ ...s, extras: { ...s.extras, ...patch } }));
  const valid = d.name.trim() !== '' && (plan !== null || /^[a-z0-9-]{2,40}$/.test(d.code)) && d.monthlyPrice !== null && d.annualPrice !== null;
  return (
    <Card
      title={plan ? t('billingUi.plans.edit', { name: plan.name }) : t('billingUi.plans.new')}
      footer={
        <>
          <Button onClick={onDone}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form className="space-y-5" onSubmit={(e) => e.preventDefault()}>
        <ErrorLine error={save.error} />
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField label={t('billingUi.plans.name')} value={d.name} maxLength={80} onChange={(e) => set({ name: e.target.value })} />
          <TextField
            label={t('billingUi.plans.code')}
            hint={t('billingUi.plans.codeHint')}
            value={d.code}
            disabled={plan !== null}
            onChange={(e) => set({ code: e.target.value.toLowerCase() })}
          />
          <MoneyField label={t('billingUi.plans.monthly')} value={d.monthlyPrice} onValue={(p) => set({ monthlyPrice: p })} />
          <MoneyField label={t('billingUi.plans.annual')} value={d.annualPrice} onValue={(p) => set({ annualPrice: p })} />
        </div>
        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-slate-900">{t('billingUi.plans.allowances')}</legend>
          <p className="text-sm text-slate-600">{t('billingUi.plans.allowancesHelp')}</p>
          <div className="grid gap-3 sm:grid-cols-4">
            <NumberField label={t('billingUi.plans.sites')} value={d.allowances.sites} onChange={(n) => setA({ sites: n })} />
            <NumberField label={t('billingUi.plans.kiosks')} value={d.allowances.kiosks} onChange={(n) => setA({ kiosks: n })} />
            <NumberField label={t('billingUi.plans.sms')} value={d.allowances.smsPerMonth} onChange={(n) => setA({ smsPerMonth: n })} />
            <NumberField label={t('billingUi.plans.storage')} value={d.allowances.storageGb} onChange={(n) => setA({ storageGb: n })} />
          </div>
          <TextField label={t('billingUi.plans.modules')} hint={t('billingUi.plans.modulesHint')} value={d.allowances.modules} onChange={(e) => setA({ modules: e.target.value })} />
        </fieldset>
        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-slate-900">{t('billingUi.plans.extras')}</legend>
          <p className="text-sm text-slate-600">{t('billingUi.plans.extrasHelp')}</p>
          <div className="grid gap-3 sm:grid-cols-3">
            <MoneyField label={t('billingUi.plans.perSite')} value={d.extras.perSite} onValue={(p) => setE({ perSite: p })} />
            <MoneyField label={t('billingUi.plans.perKiosk')} value={d.extras.perKiosk} onValue={(p) => setE({ perKiosk: p })} />
            <MoneyField label={t('billingUi.plans.perStorage')} value={d.extras.perStorageGb} onValue={(p) => setE({ perStorageGb: p })} />
            <NumberField label={t('billingUi.plans.smsBundle')} min={1} value={d.extras.smsBundleSize} onChange={(n) => setE({ smsBundleSize: n })} />
            <MoneyField label={t('billingUi.plans.perSmsBundle')} value={d.extras.perSmsBundle} onValue={(p) => setE({ perSmsBundle: p })} />
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-slate-800">{t('billingUi.plans.addOnPrices')}</h3>
            {d.modulePrices.map((m, i) => (
              <div key={i} className="grid items-end gap-3 sm:grid-cols-[1fr_1fr_auto]">
                <TextField
                  label={t('billingUi.plans.addOnKey')}
                  value={m.key}
                  onChange={(e) => set({ modulePrices: d.modulePrices.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)) })}
                />
                <MoneyField
                  label={t('billingUi.plans.addOnPrice')}
                  value={m.price}
                  onValue={(p) => set({ modulePrices: d.modulePrices.map((x, j) => (j === i ? { ...x, price: p } : x)) })}
                />
                <Button onClick={() => set({ modulePrices: d.modulePrices.filter((_, j) => j !== i) })} aria-label={t('billingUi.plans.removeAddOn', { n: i + 1 })}>
                  {t('billingUi.remove')}
                </Button>
              </div>
            ))}
            <Button onClick={() => set({ modulePrices: [...d.modulePrices, { key: '', price: 0 }] })}>{t('billingUi.plans.addAddOn')}</Button>
          </div>
        </fieldset>
        {plan ? <CheckboxField label={t('billingUi.plans.active')} hint={t('billingUi.plans.activeHint')} checked={d.active} onChange={(e) => set({ active: e.target.checked })} /> : null}
        <p className="text-sm text-slate-600">{t('billingUi.plans.priceNote')}</p>
      </form>
    </Card>
  );
}

/** Platform: plans with allowances and extra prices. Price changes apply from each subscription's next period. */
export function PlansPage() {
  const canManage = useCan('platform.billing_manage');
  const [editing, setEditing] = useState<Plan | 'new' | null>(null);
  const q = useQuery({ queryKey: keys.plans, queryFn: () => api.get(`${PLAT}/plans`, { schema: PlanArray }) });
  return (
    <>
      <PageHeader
        title={t('billingUi.plans.title')}
        description={t('billingUi.plans.description')}
        actions={canManage ? <Button variant="primary" onClick={() => setEditing('new')}>{t('billingUi.plans.new')}</Button> : null}
      />
      <BillingTabs />
      {editing ? (
        <div className="mb-5">
          <PlanEditor key={editing === 'new' ? 'new' : editing.id} plan={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />
        </div>
      ) : null}
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <TableWrap caption={t('billingUi.plans.table')}>
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <Th>{t('billingUi.plans.name')}</Th>
              <Th>{t('billingUi.plans.monthly')}</Th>
              <Th>{t('billingUi.plans.annual')}</Th>
              <Th>{t('billingUi.plans.includes')}</Th>
              <Th>{t('billingUi.plans.customers')}</Th>
              <Th>{t('common.status')}</Th>
              <Th>{t('common.actions')}</Th>
            </tr>
          </thead>
          <tbody>
            {q.data.length === 0 ? <EmptyRow cols={7} /> : null}
            {q.data.map((p) => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">{p.name}</td>
                <td className="px-4 py-3"><Money pence={p.monthlyPrice} /></td>
                <td className="px-4 py-3"><Money pence={p.annualPrice} /></td>
                <td className="px-4 py-3 text-slate-700">
                  {t('billingUi.plans.summary', { sites: p.allowances.sites, kiosks: p.allowances.kiosks, sms: p.allowances.smsPerMonth, gb: p.allowances.storageGb })}
                </td>
                <td className="px-4 py-3">{p.subscriptionCount}</td>
                <td className="px-4 py-3"><Badge tone={p.active ? 'good' : 'neutral'}>{p.active ? t('billingUi.plans.statusActive') : t('billingUi.plans.statusRetired')}</Badge></td>
                <td className="px-4 py-3">
                  {canManage ? <Button onClick={() => setEditing(p)} aria-label={t('billingUi.plans.editLabel', { name: p.name })}>{t('common.edit')}</Button> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </>
  );
}
